import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Cell,
} from "recharts";
import { ArrowUpRight, Target, UserRound, Sparkles, ListChecks, TrendingUp, IndianRupee } from "lucide-react";
import { divisionLabel } from "@/lib/roles";

const currency = (v) => `₹${(v ?? 0).toLocaleString("en-IN")}`;

// Chart style constants — extracted so React doesn't recreate them on every
// render (avoids unnecessary re-mounts of recharts primitives).
const AXIS_TICK       = { fontSize: 11, fill: "#475569" };
const AXIS_TICK_BOLD  = { fontSize: 12, fill: "#475569" };
const TOOLTIP_STYLE   = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8 };
const BAR_RADIUS      = [8, 8, 0, 0];
const DIVISION_COLORS = ["#041B46", "#0EA5FF", "#14346E", "#5FBF3A", "#0284C7"];
const FUNNEL_COLORS   = ["#0EA5FF", "#0284C7", "#14346E", "#041B46", "#5FBF3A", "#94A3B8"];

const KPI_META = [
  { key: "total_leads",         label: "Total Leads",        icon: Target,     accent: "#0EA5FF" },
  { key: "total_contacts",      label: "Contacts",           icon: UserRound,  accent: "#041B46" },
  { key: "total_opportunities", label: "Opportunities",      icon: Sparkles,   accent: "#5FBF3A" },
  { key: "open_tasks",          label: "Open Tasks",         icon: ListChecks, accent: "#F59E0B" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
  }, []);

  const k = data?.kpis || {};

  return (
    <AppLayout title="Executive Dashboard">
      {/* Top-left North Star + KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <div
          data-testid="dashboard-north-star"
          className="lg:col-span-1 rounded-2xl p-6 text-white relative overflow-hidden miro-grain"
          style={{ background: "var(--miro-navy)" }}
        >
          <div className="absolute inset-0" style={{
            background: "radial-gradient(400px at 90% 10%, rgba(14,165,255,0.35), transparent 60%)"
          }}/>
          <div className="relative z-10">
            <div className="miro-overline text-[var(--miro-blue)]">Weighted pipeline</div>
            <div className="font-display font-black text-4xl xl:text-5xl mt-2 tracking-tight">
              {currency(k.pipeline_value)}
            </div>
            <div className="text-sm text-white/70 mt-2">
              Across {k.total_opportunities || 0} live opportunities, weighted by close probability.
            </div>
            <div className="mt-6 flex items-center gap-5 text-xs">
              <div>
                <div className="text-white/50">Won revenue</div>
                <div className="font-display font-bold text-lg text-[var(--miro-green)] flex items-center gap-1">
                  <IndianRupee size={14}/>{(k.won_revenue ?? 0).toLocaleString("en-IN")}
                </div>
              </div>
              <div>
                <div className="text-white/50">Conversion</div>
                <div className="font-display font-bold text-lg flex items-center gap-1">
                  {k.conversion_rate ?? 0}% <TrendingUp size={14} className="text-[var(--miro-green)]"/>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 grid grid-cols-2 gap-5">
          {KPI_META.map((m) => {
            const Icon = m.icon;
            return (
              <div key={m.key} data-testid={`kpi-${m.key}`} className="miro-card p-5">
                <div className="flex items-start justify-between">
                  <div className="miro-overline">{m.label}</div>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                       style={{ background: `${m.accent}18`, color: m.accent }}>
                    <Icon size={17}/>
                  </div>
                </div>
                <div className="font-display font-black text-3xl mt-3 text-[var(--miro-navy)]">
                  {k[m.key] ?? "—"}
                </div>
                <div className="text-xs text-[var(--miro-text-muted)] mt-1 flex items-center gap-1">
                  <ArrowUpRight size={12}/> live count
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 miro-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="miro-overline">14-day trend</div>
              <h3 className="font-display font-bold text-lg text-[var(--miro-navy)]">Leads created</h3>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={data?.trend || []}>
                <defs>
                  <linearGradient id="fillLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0EA5FF" stopOpacity={0.5}/>
                    <stop offset="100%" stopColor="#0EA5FF" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E2E8F0" vertical={false}/>
                <XAxis dataKey="date" tick={AXIS_TICK} axisLine={false} tickLine={false}/>
                <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={TOOLTIP_STYLE}/>
                <Area type="monotone" dataKey="leads" stroke="#0EA5FF" strokeWidth={2} fill="url(#fillLeads)"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="miro-card p-6">
          <div className="miro-overline">Conversion funnel</div>
          <h3 className="font-display font-bold text-lg text-[var(--miro-navy)]">Lead status</h3>
          <div className="mt-4 space-y-2">
            {(data?.funnel || []).map((row, i) => {
              const max = Math.max(...(data?.funnel || []).map(r => r.count), 1);
              const pct = Math.round((row.count / max) * 100);
              return (
                <div key={row.status} data-testid={`funnel-${row.status}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="capitalize font-medium text-[var(--miro-text-secondary)]">{row.status}</span>
                    <span className="font-mono text-[var(--miro-navy)]">{row.count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-[var(--miro-bg)] overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                         style={{ width: `${pct}%`, background: FUNNEL_COLORS[i] || "#0EA5FF" }}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Division breakdown */}
      <div className="miro-card p-6 mt-5">
        <div className="miro-overline">By division</div>
        <h3 className="font-display font-bold text-lg text-[var(--miro-navy)] mb-4">Leads across MIRO divisions</h3>
        <div className="h-56">
          <ResponsiveContainer>
            <BarChart data={(data?.by_division || []).map(d => ({ ...d, label: divisionLabel(d.division) }))}>
              <CartesianGrid stroke="#E2E8F0" vertical={false}/>
              <XAxis dataKey="label" tick={AXIS_TICK_BOLD} axisLine={false} tickLine={false}/>
              <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false}/>
              <Tooltip contentStyle={TOOLTIP_STYLE}/>
              <Bar dataKey="count" radius={BAR_RADIUS}>
                {(data?.by_division || []).map((entry, i) => (
                  <Cell key={entry.division || `div-${i}`} fill={DIVISION_COLORS[i % DIVISION_COLORS.length]}/>
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </AppLayout>
  );
}
